import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useDocumentTitle } from '@/components/common';
import { useAuth } from '@/features/auth';
import * as authLib from '@/lib/auth';
import { getHomeRedirectTarget } from '@/lib/home-redirect';

// Snapes primary palette (tailwind.config.ts).
const PRIMARY = '#03a0c6';
const PRIMARY_DARK = '#028aa8';
const PRIMARY_CONTAINER = '#c8eef7';
const SECONDARY_CONTAINER = '#ffe1cf';
const SURFACE = '#ffffff';
const BACKGROUND = '#f8f9fb';
const ON_SURFACE = '#0f1f23';
const ON_SURFACE_VARIANT = '#475458';
const OUTLINE_VARIANT = '#cbd6da';

export function NotFoundPage() {
  useDocumentTitle('404 — Not Found');
  const navigate = useNavigate();
  const auth = useAuth();
  const routerState = useRouterState();

  const storedUser = authLib.getStoredUser();
  const currentUser = auth?.user || storedUser;
  const isAuthenticated = Boolean(
    auth?.isAuthenticated || authLib.isAuthenticated()
  );
  const roles = useMemo(() => currentUser?.roles ?? [], [currentUser]);

  const livePath = routerState.location.pathname + routerState.location.search;
  const attempted = livePath && livePath !== '/not-found' ? livePath : '/unknown';

  const handleHome = useCallback(() => {
    navigate({
      to: getHomeRedirectTarget({ isAuthenticated, roles }),
      replace: true,
    });
  }, [isAuthenticated, roles, navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleHome();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleHome]);

  const body = "'Inter', system-ui, -apple-system, sans-serif";

  return (
    <div
      className="flex h-screen w-screen flex-col overflow-hidden antialiased"
      style={{
        backgroundColor: BACKGROUND,
        color: ON_SURFACE,
        fontFamily: body,
      }}
    >
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }
        @keyframes ringPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50%      { transform: scale(1.04); opacity: 0.85; }
        }
        .hero-float { animation: heroFloat 4.5s ease-in-out infinite; }
        .ring-pulse-1 { animation: ringPulse 5s ease-in-out infinite; }
        .ring-pulse-2 { animation: ringPulse 6s ease-in-out infinite reverse; }
      `}</style>

      <main className="relative z-10 mx-auto flex w-full max-w-[1440px] flex-grow items-center justify-center p-4 md:p-10">
        {/* Decorative background blobs */}
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden opacity-60">
          <div
            className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full"
            style={{
              backgroundColor: PRIMARY_CONTAINER,
              filter: 'blur(120px)',
            }}
          />
          <div
            className="absolute bottom-1/4 right-1/4 h-64 w-64 rounded-full"
            style={{
              backgroundColor: SECONDARY_CONTAINER,
              filter: 'blur(100px)',
            }}
          />
        </div>

        {/* Card */}
        <div
          className="relative z-10 grid w-full max-w-4xl grid-cols-1 items-stretch gap-0 overflow-hidden rounded-xl border md:grid-cols-2"
          style={{
            backgroundColor: SURFACE,
            borderColor: OUTLINE_VARIANT,
            boxShadow:
              '0 10px 25px rgba(15, 23, 42, 0.06), 0 2px 6px rgba(15, 23, 42, 0.04)',
          }}
        >
          {/* Content side */}
          <div className="z-10 flex flex-col justify-center gap-6 p-8 text-center md:p-10 md:text-left">
            <div>
              <h1
                style={{
                  fontSize: '48px',
                  lineHeight: '56px',
                  letterSpacing: '-0.02em',
                  fontWeight: 700,
                  color: PRIMARY,
                  marginBottom: '8px',
                }}
              >
                404
              </h1>
              <h2
                style={{
                  fontSize: '32px',
                  lineHeight: '40px',
                  letterSpacing: '-0.01em',
                  fontWeight: 600,
                  color: ON_SURFACE,
                }}
              >
                Lost in Transit?
              </h2>
            </div>

            <p
              className="mx-auto md:mx-0"
              style={{
                fontSize: '18px',
                lineHeight: '28px',
                fontWeight: 400,
                color: ON_SURFACE_VARIANT,
                maxWidth: '28rem',
              }}
            >
              The page you are looking for isn&rsquo;t here. Let&rsquo;s get you
              back on track to managing your supply chain efficiently.
            </p>

            <div
              className="mx-auto md:mx-0"
              style={{
                fontSize: '13px',
                lineHeight: '18px',
                fontFamily:
                  "'JetBrains Mono', ui-monospace, Menlo, Monaco, monospace",
                color: ON_SURFACE_VARIANT,
              }}
            >
              <span style={{ opacity: 0.75 }}>Couldn&rsquo;t locate:</span>{' '}
              <span
                style={{
                  color: PRIMARY_DARK,
                  backgroundColor: 'rgba(3, 160, 198, 0.08)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  wordBreak: 'break-all',
                }}
              >
                {attempted}
              </span>
            </div>

            <div className="mt-2 flex flex-col justify-center gap-4 sm:flex-row md:justify-start">
              <button
                type="button"
                onClick={handleHome}
                className="flex h-12 items-center justify-center gap-2 rounded-lg px-6 shadow-sm transition-all hover:shadow-md active:scale-95"
                style={{
                  backgroundColor: PRIMARY,
                  color: '#ffffff',
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = PRIMARY_DARK;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = PRIMARY;
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: '20px',
                    fontVariationSettings: "'FILL' 1",
                  }}
                  aria-hidden
                >
                  home
                </span>
                Go Home
              </button>
            </div>

            <span
              className="mx-auto md:mx-0"
              style={{
                fontSize: '12px',
                color: ON_SURFACE_VARIANT,
                letterSpacing: '0.05em',
                opacity: 0.7,
              }}
            >
              or press{' '}
              <kbd
                className="rounded border px-1.5 py-0.5"
                style={{
                  borderColor: OUTLINE_VARIANT,
                  fontSize: '10px',
                  letterSpacing: '0.05em',
                }}
              >
                Enter
              </kbd>
            </span>
          </div>

          {/* Hero panel — gradient with single large icon */}
          <HeroPanel icon="wrong_location" />
        </div>
      </main>
    </div>
  );
}

interface HeroPanelProps {
  icon: string;
}

function HeroPanel({ icon }: HeroPanelProps) {
  return (
    <div
      className="relative flex min-h-[280px] items-center justify-center overflow-hidden md:min-h-full"
      style={{
        background: `radial-gradient(135% 100% at 80% 20%, ${PRIMARY_CONTAINER} 0%, #eef9fc 55%, #ffffff 100%)`,
      }}
    >
      {/* Subtle dotted texture for depth */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(3, 160, 198, 0.10) 1px, transparent 1.5px)',
          backgroundSize: '20px 20px',
          maskImage:
            'radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 90%)',
          opacity: 0.55,
        }}
      />

      {/* Concentric rings */}
      <div
        className="ring-pulse-1 absolute"
        style={{
          width: '260px',
          height: '260px',
          borderRadius: '50%',
          border: `1px solid rgba(3, 160, 198, 0.20)`,
        }}
      />
      <div
        className="ring-pulse-2 absolute"
        style={{
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          border: `1px solid rgba(3, 160, 198, 0.28)`,
        }}
      />
      <div
        className="absolute"
        style={{
          width: '110px',
          height: '110px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          boxShadow: '0 8px 24px rgba(3, 160, 198, 0.15)',
        }}
      />

      {/* The hero icon */}
      <span
        className="hero-float material-symbols-outlined relative z-10"
        style={{
          fontSize: '88px',
          color: PRIMARY,
          fontVariationSettings: "'FILL' 1",
          filter: 'drop-shadow(0 6px 14px rgba(3, 160, 198, 0.25))',
        }}
        aria-hidden
      >
        {icon}
      </span>

      {/* Soft right-edge fade for cohesion with the card */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-12 md:hidden"
        style={{
          background: `linear-gradient(to right, ${SURFACE}, transparent)`,
        }}
      />
    </div>
  );
}
